import React, { ReactElement, useContext } from 'react';
import { css } from '@emotion/react';
import { BsPlus } from '@react-icons/all-files/bs/BsPlus';
import { useContextSelector } from 'use-context-selector';
import { Tab } from '@bucketplace/design-system';

import { StickyTabThemeContext } from '@/contexts/internal/StickyTabThemeContext';

import { EditorPlaceholder } from '../../../components';
import { EditorContext, useEditorInfoContext } from '../../../contexts';
import { StickyTabItemProps } from '../StickyTabItem/StickyTabItem';

export type TextTabItemProps = Pick<StickyTabItemProps, 'id' | 'logData' | 'onClick'> & {
  /** 탭 타이틀 */
  text?: string,
};

export function TextTabItem({
  id,
  text,
  logData,
  onClick,
}: TextTabItemProps): ReactElement {
  const { onSendLog } = useEditorInfoContext();
  const theme = useContext(StickyTabThemeContext);
  const isEditMode = useContextSelector(EditorContext, (context) => context?.getState().displayMode === 'edit');

  const handleTabClick = (): void => {
    if (id == null) {
      return;
    }
    if (logData) {
      onSendLog?.(logData);
    }
    onClick(id);
  };

  if (!text || text.length === 0) {
    return (
      <EditorPlaceholder
        icon={<BsPlus />}
        message="탭 타이틀을 입력해주세요."
      />
    );
  }

  return (
    <Tab.Item
      css={[
        theme.default && css`
          color: ${theme.default};
        `,
        theme.selected && css`
          &:after {
            background-color: ${theme.selected};
          }

          &[data-state='active'] {
            color: ${theme.selected};
          }

          &.focus-visible:focus {
            color: ${theme.selected};
          }

          @media (hover: hover) and (pointer: fine) {
            &:hover, &:active {
              color: ${theme.selected};
            }
          }
        `,
        isEditMode && css`
          width: 100%;
        `,
      ]}
      id={id}
      value={id!}
      onClick={handleTabClick}
    >
      <Tab.ItemSlot
        css={css`
          max-width: 100%;
        `}
        side="center"
      >
        <Tab.ItemLabel
          css={css`
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          { text }
        </Tab.ItemLabel>
      </Tab.ItemSlot>
    </Tab.Item>
  );
}
